import { CalendlyAppointment } from '@/features/appointments/types/CalendlyAppointment';
import { filterAppointments } from '@/features/appointments/utils/filterAppointments';
import { parseAppointments } from '@/features/appointments/utils/parseAppointments';
import { BASE_URL } from '@/utils/Constants';
import { AppointmentStatus } from '@/utils/enums/Consults';
import { useAuthStore } from '@/utils/stores/useAuthStore';
import { useExpertsStore } from '@/utils/stores/useExpertsStore';
import { useUserStore } from '@/utils/stores/useUserStore';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

export const useVenereAppointments = () => {
  const { auth } = useAuthStore();
  const idToken = auth.idToken;
  const { userData } = useUserStore();
  const email = userData.email;
  const { experts } = useExpertsStore();
  const { specialist, physician, nutritionist } = experts;

  const getAppointments = useCallback(async (idToken: string, email: string): Promise<CalendlyAppointment[] | null> => {
    try {
      const response = await fetch(`${BASE_URL}/api/booking/appointments/${email}`, {
        method: 'GET',
        headers: {
          'Authorization': idToken
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else if (response.status === 401) {
        throw new Error("Session expired");
      } else if (response.status === 404) {
        console.warn(`Nessun appuntamento trovato per user: ${email}`);
        return null;
      } else {
        throw new Error("Errore nel recuperare gli appuntamenti");
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("Session expired")) {
          throw new Error("Session expired");
        } else if (error.message.includes("Nessun appuntamento trovato")) {
          throw new Error(error.message);
        }
      }
      throw new Error("An unexpected error occurred");
    }
  }, []);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['venere-appointments', email, idToken],
    queryFn: () => getAppointments(idToken!, email!),
    enabled: !!idToken && !!email,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000, // 1 minute
    retry: 2,
  });

  // Parse appointments when data is available
  const parsedAppointments = useMemo(() => {
    
    if (!data) {
      return { allAppointments: [] };
    }
    
    // Check if we have at least some experts available
    const availableExperts = [specialist, nutritionist, physician].filter(Boolean);
    if (availableExperts.length === 0) {
      return { allAppointments: [] };
    }
    
    // Use available experts, pass null for missing ones
    const parsed = parseAppointments(data, specialist || null, nutritionist || null, physician || null);
    
    // If we have appointments but some couldn't be parsed due to missing experts,
    // we should still show the ones that were parsed successfully
    if (parsed.allAppointments.length === 0 && data.length > 0) {
    }
    
    return parsed;
  }, [data, specialist, nutritionist, physician]);

  // Filter appointments by status and time
  const appointments = useMemo(() => {
    const { allAppointments } = parsedAppointments;
    
    const active = filterAppointments.byStatus(allAppointments, AppointmentStatus.Active);
    const future = active.filter(filterAppointments.isFuture);
    const past = active.filter(filterAppointments.isPast);
    
    return {
      all: allAppointments,
      active,
      future,
      past,
    };
  }, [parsedAppointments]);

  // Filter by expert type
  const specialistAppointments = useMemo(() => {
    return appointments.future.filter(filterAppointments.isExpert);
  }, [appointments.future]);

  const nutritionistAppointments = useMemo(() => {
    return appointments.future.filter(filterAppointments.isNutritionist);
  }, [appointments.future]);

  // Helper functions
  const getUpcomingAppointments = useCallback((count: number = 5) => {
    return appointments.future.slice(0, count);
  }, [appointments.future]);

  const getRecentAppointments = useCallback((count: number = 5) => {
    return appointments.past.slice(0, count);
  }, [appointments.past]);

  const getAppointmentsByExpert = useCallback((expertEmail: string) => {
    return appointments.all.filter(appointment => 
      appointment.expert.email === expertEmail
    );
  }, [appointments.all]);

  const returnValue = {
    // Raw data
    rawAppointments: data || [],
    
    // Parsed appointments
    appointments: appointments.all,
    activeAppointments: appointments.active,
    futureAppointments: appointments.future,
    pastAppointments: appointments.past,
    
    // Filtered by expert type
    specialistAppointments,
    nutritionistAppointments,
    
    // Helper functions
    getUpcomingAppointments,
    getRecentAppointments,
    getAppointmentsByExpert,
    
    // Query state
    isLoading,
    isFetching,
    error,
    refetch,
    
    // Computed values
    hasUpcomingAppointments: appointments.future.length > 0,
    hasPastAppointments: appointments.past.length > 0,
    totalAppointments: appointments.all.length,
    upcomingCount: appointments.future.length,
    pastCount: appointments.past.length,
  };

  return returnValue;
}; 